import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Typography
} from '@mui/material';
import { useFormContext } from 'react-hook-form';

import Input from '../ui/forms/fields/input';
import Select from '../ui/forms/fields/select';
import Checkbox from '../ui/forms/fields/checkbox';
import DatePicker from '../ui/forms/fields/datePicker';
import { getGameType } from '../../constants/gameConstants';

const PlayersImportSection = ({ saving, playersImport }) => {
  const { control, register } = useFormContext();
  const {
    watchImportPlayers,
    sortedImportEvents,
    currentLogLoading,
    maxImportTeams,
    importRangeError,
    importSkipError,
    firstSectionTeamCount,
    showAutoGroupCheckbox,
    targetImportGameType,
    watchImportAutoGroupPlayers,
    importCompatibilityError,
    watchImportShowPastEvents,
    watchImportOrderByPbaRanking,
    pbaRankingsMap,
    pbaRankingsLoading,
    pbaRankingsError,
    preview
  } = playersImport;

  const [previewOpen, setPreviewOpen] = useState(false);

  const previewDisabled = Boolean(
    importRangeError || importSkipError || importCompatibilityError || !preview
  );
  const isAronSherriff = (player) => {
    const name = (player?.name || '').trim();
    const surname = (player?.surname || '').trim();
    return name.toLowerCase() === 'aron' && surname.toLowerCase() === 'sherriff';
  };

  const previewPlayerList = useMemo(() => {
    if (!preview?.players?.length) {
      return [];
    }

    return preview.players
      .map((player) => {
        const fullName = `${player.name} ${player.surname}`.trim();
        if (!fullName) {
          return null;
        }

        if (!watchImportOrderByPbaRanking) {
          return fullName;
        }

        // Aron Sherriff always shows as rank 0
        if (isAronSherriff(player)) {
          return `${fullName} [0]`;
        }

        const key = player?.bsaNumber ?? player?.bsa_number;
        const apiRank = key != null ? pbaRankingsMap.get(String(key).trim()) : null;
        const fallbackRank = player?.pbaRanking ?? player?.ranking ?? player?.rank;
        const rankValue = apiRank ?? fallbackRank;
        return rankValue != null ? `${fullName} [${rankValue}]` : fullName;
      })
      .filter(Boolean);
  }, [preview, watchImportOrderByPbaRanking, pbaRankingsMap]);

  const previewTeamList = useMemo(() => {
    if (!preview?.teams?.length) {
      return [];
    }

    return preview.teams
      .slice(0, 10)
      .map((team) => team.name)
      .filter(Boolean);
  }, [preview]);

  const pbaMatchCount = useMemo(() => {
    if (!watchImportOrderByPbaRanking || !preview?.players?.length) {
      return 0;
    }

    return preview.players.reduce((total, player) => {
      const key = player?.bsaNumber ?? player?.bsa_number;
      if (key == null) {
        return total;
      }

      return pbaRankingsMap.has(String(key).trim()) ? total + 1 : total;
    }, 0);
  }, [preview, watchImportOrderByPbaRanking, pbaRankingsMap]);

  const pbaUnmatchedPlayers = useMemo(() => {
    if (!watchImportOrderByPbaRanking || !preview?.players?.length) {
      return [];
    }

    const unmatched = preview.players.filter((player) => {
      const key = player?.bsaNumber ?? player?.bsa_number;
      return key == null || !pbaRankingsMap.has(String(key).trim());
    });

    return unmatched.sort((a, b) => {
      const isAronA =
        `${a.name}`.toLowerCase().trim() === 'aron' &&
        `${a.surname}`.toLowerCase().trim() === 'sherriff';
      const isAronB =
        `${b.name}`.toLowerCase().trim() === 'aron' &&
        `${b.surname}`.toLowerCase().trim() === 'sherriff';
      if (isAronA) return -1;
      if (isAronB) return 1;
      return 0;
    });
  }, [preview, watchImportOrderByPbaRanking, pbaRankingsMap]);

  return (
    <Stack mt={3} gap={2}>
      <Typography variant="h6" component="h2">
        Players
      </Typography>

      <Checkbox
        name={'importPlayers'}
        label="Import players and teams from another event"
        control={control}
        disabled={saving}
      />

      {watchImportPlayers && (
        <Stack gap={2}>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <Select
              label="Import From"
              name="importFromEventId"
              control={control}
              disabled={saving}
              defaultValue=""
              sx={{ maxWidth: 360, minWidth: 240 }}>
              {sortedImportEvents.map((evt) => (
                <MenuItem key={evt.id} value={evt.id}>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="body2">{evt.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {getGameType(evt.gameFormat?.gameType) || 'Unknown type'} ·{' '}
                      {evt.teams?.length || 0} team{(evt.teams?.length || 0) === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <Checkbox
              name={'importShowPastEvents'}
              label="Show past events"
              control={control}
              disabled={saving}
            />
          </Box>

          <Select
            label="Import Log"
            name="importLogType"
            control={control}
            disabled={saving}
            defaultValue="base">
            <MenuItem value={'base'}>Base Log</MenuItem>
            <MenuItem value={'current'}>Current Log</MenuItem>
          </Select>

          <Box display="flex" gap={2} flexWrap="wrap">
            <Input
              label="From Position"
              type="number"
              disabled={saving || currentLogLoading}
              inputProps={{ min: 1, max: maxImportTeams || undefined }}
              error={Boolean(importRangeError)}
              helperText={importRangeError}
              {...register('importPositionFrom', { valueAsNumber: true })}
              sx={{ maxWidth: 200 }}
            />
            <Input
              label="To Position"
              type="number"
              disabled={saving || currentLogLoading}
              inputProps={{ min: 1, max: maxImportTeams || undefined }}
              error={Boolean(importRangeError)}
              helperText={importRangeError}
              {...register('importPositionTo', { valueAsNumber: true })}
              sx={{ maxWidth: 200 }}
            />
            <Input
              label="Skip per section"
              type="number"
              disabled={saving || currentLogLoading}
              inputProps={{ min: 0, max: firstSectionTeamCount || undefined }}
              error={Boolean(importSkipError)}
              helperText={
                importSkipError ||
                (firstSectionTeamCount ? `Max ${firstSectionTeamCount}` : undefined)
              }
              {...register('importSkipSectionPositions', { valueAsNumber: true })}
              sx={{ maxWidth: 200 }}
            />
          </Box>

          {showAutoGroupCheckbox && (
            <Box>
              <Checkbox
                name={'importAutoGroupPlayers'}
                label={`Auto-group players into ${getGameType(targetImportGameType)} teams`}
                control={control}
                disabled={saving}
              />
              {!watchImportAutoGroupPlayers && (
                <Alert severity="info" variant="outlined" sx={{ mt: 1, color: 'info.main' }}>
                  Players will be imported without teams. You can create teams later.
                </Alert>
              )}
            </Box>
          )}

          <Checkbox
            name={'importOrderByPbaRanking'}
            label="Order/import players by PBA Ranking"
            control={control}
            disabled={saving}
          />
          {watchImportOrderByPbaRanking && (
            <Stack gap={1}>
              <DatePicker
                name="importPbaRankingDate"
                label="PBA Ranking Date (optional)"
                control={control}
                disabled={saving}
                format="DD/MM/YYYY"
                sx={{ maxWidth: 220 }}
              />
              <Typography variant="caption" color="textSecondary">
                {pbaRankingsLoading
                  ? 'Loading PBA rankings...'
                  : pbaRankingsError ||
                    `PBA matches: ${pbaMatchCount}/${preview?.players?.length || 0}`}
              </Typography>
            </Stack>
          )}

          {importCompatibilityError && (
            <Typography variant="caption" color="error">
              {importCompatibilityError}
            </Typography>
          )}

          <Button
            variant="outlined"
            color="info"
            size="small"
            sx={{ alignSelf: 'flex-start' }}
            disabled={previewDisabled}
            onClick={() => setPreviewOpen(true)}>
            Preview Import
          </Button>
        </Stack>
      )}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Preview</DialogTitle>
        <DialogContent>
          {preview ? (
            <Stack gap={1} mt={1}>
              <Typography variant="body2">Source event: {preview.sourceEventName}</Typography>
              <Typography variant="body2">Log type: {preview.logType}</Typography>
              <Typography variant="body2">
                Range: {preview.positionFrom} to {preview.positionTo}
              </Typography>
              <Typography variant="body2">
                Skip per section: {preview.skipSectionPositions}
              </Typography>
              <Typography variant="body2">Selected teams: {preview.selectedTeamsCount}</Typography>
              <Typography variant="body2">Players: {preview.players.length}</Typography>
              <Typography variant="body2">Teams to add: {preview.teams.length}</Typography>
              <Typography variant="body2">
                Target game type: {getGameType(preview.targetGameType)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Players preview:
              </Typography>
              {previewPlayerList.length ? (
                <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                  {previewPlayerList.map((playerName, index) => (
                    <Box component="li" key={`${playerName}-${index}`} sx={{ mb: 0.25 }}>
                      <Typography variant="body2">
                        {index + 1}. {playerName}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2">None</Typography>
              )}
              <Typography variant="body2" sx={{ mt: 1 }}>
                Teams preview:
              </Typography>
              {previewTeamList.length ? (
                <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                  {previewTeamList.map((teamName, index) => (
                    <Box component="li" key={`${teamName}-${index}`} sx={{ mb: 0.25 }}>
                      <Typography variant="body2">
                        {index + 1}. {teamName}
                      </Typography>
                    </Box>
                  ))}
                  {preview.teams.length > previewTeamList.length && (
                    <Box component="li">
                      <Typography variant="body2">
                        + {preview.teams.length - previewTeamList.length} more
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body2">None</Typography>
              )}
              {preview.teams.length === 0 && (
                <Alert severity="info" variant="outlined" sx={{ mt: 1, color: 'info.main' }}>
                  Teams will not be created with the current settings.
                </Alert>
              )}
              {watchImportOrderByPbaRanking && pbaUnmatchedPlayers.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight={700} color="warning.main">
                    Not in PBA ranking ({pbaUnmatchedPlayers.length}):
                  </Typography>
                  <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                    {(() => {
                      const hasAron = pbaUnmatchedPlayers.some(
                        (p) =>
                          `${p.name}`.toLowerCase().trim() === 'aron' &&
                          `${p.surname}`.toLowerCase().trim() === 'sherriff'
                      );
                      let nonAronIndex = 0;
                      return pbaUnmatchedPlayers.map((player, index) => {
                        const fullName = `${player.name} ${player.surname}`.trim();
                        const bsa = player?.bsaNumber ?? player?.bsa_number;
                        const isAron =
                          `${player.name}`.toLowerCase().trim() === 'aron' &&
                          `${player.surname}`.toLowerCase().trim() === 'sherriff';
                        let importPosition;
                        if (isAron) {
                          importPosition = 1;
                        } else {
                          // Shift positions by 1 if Aron is present (he takes slot 1)
                          importPosition = (hasAron ? 1 : 0) + pbaMatchCount + nonAronIndex + 1;
                          nonAronIndex++;
                        }
                        return (
                          <Box component="li" key={player.id ?? index} sx={{ mb: 0.25 }}>
                            <Typography variant="body2">
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ fontWeight: 600, mr: 0.5 }}>
                                {importPosition}.
                              </Typography>
                              {fullName}
                              {bsa != null ? (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 0.75 }}>
                                  BSA: {bsa}
                                </Typography>
                              ) : (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="error.main"
                                  sx={{ ml: 0.75 }}>
                                  No BSA number
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        );
                      });
                    })()}
                  </Box>
                </Box>
              )}
            </Stack>
          ) : (
            <Typography variant="body2">No preview available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default PlayersImportSection;
